import type { ReactNode } from "react";

import { SpanIcon } from "./SpanIcon";
import { formatDurationLabel, formatSpanTypeLabel, getSpanDisplayName } from "./utils";

import { ProviderIcon } from "@/components/ProviderWithLogo/ProviderIcon";
import type { SpanNode } from "@/types/spans";

const STATUS_COLORS: Record<string, string> = {
  Error: "text-red-600",
  Failed: "text-red-600",
  "Partially Failed": "text-amber-600",
  Running: "text-blue-600",
};

const toCount = (value: unknown): number | null => {
  if (value === null || value === undefined || value === "") {
    return null;
  }
  const count = Number(value);
  return Number.isFinite(count) ? count : null;
};

const formatPrice = (price: number): string => `$${Number(price.toFixed(4))}`;

const InfoRow = ({
  label,
  value,
  emphasized = false,
}: {
  label: string;
  value: ReactNode;
  emphasized?: boolean;
}) => (
  <div
    className={`flex items-center justify-between gap-4 ${
      emphasized ? "border-t border-gray-100 pt-1.5 font-semibold text-gray-900" : ""
    }`}
  >
    <dt className={emphasized ? "shrink-0" : "shrink-0 text-gray-400"}>{label}</dt>
    <dd className={`min-w-0 text-right font-medium ${emphasized ? "" : "text-gray-700"}`}>
      {value}
    </dd>
  </div>
);

export const SpanHoverCard = ({
  spanNode,
  status,
  typeValue,
  durationMs,
  isPromptLayerTool = false,
}: {
  spanNode: SpanNode;
  status: string;
  typeValue: string;
  durationMs: number;
  isPromptLayerTool?: boolean;
}) => {
  const meta = spanNode.request_log_meta;
  const attributes = spanNode.attributes ?? {};

  const model = meta?.model ?? attributes["gen_ai.request.model"];
  const providerRaw = meta?.provider ?? attributes["gen_ai.provider.name"];
  const provider = typeof providerRaw === "string" ? providerRaw.split(".")[0] : null;
  const inputTokens = toCount(meta?.input_tokens ?? attributes["gen_ai.usage.input_tokens"]);
  const outputTokens = toCount(meta?.output_tokens ?? attributes["gen_ai.usage.output_tokens"]);
  const cachedTokens = toCount(attributes["gen_ai.usage.cache_read.input_tokens"]);
  const totalTokens =
    inputTokens !== null || outputTokens !== null
      ? (inputTokens ?? 0) + (outputTokens ?? 0)
      : toCount(meta?.tokens);
  const price = meta?.price ?? null;

  const typeLabel = typeValue && typeValue !== "Unknown" ? formatSpanTypeLabel(typeValue) : null;
  const subtitle = [typeLabel, formatDurationLabel(durationMs)].filter(Boolean).join(" · ");

  let totalRow: { label: string; value: string } | null = null;
  if (totalTokens !== null && price !== null) {
    totalRow = { label: "Total tokens / price", value: `${totalTokens} / ${formatPrice(price)}` };
  } else if (totalTokens !== null) {
    totalRow = { label: "Total tokens", value: String(totalTokens) };
  } else if (price !== null) {
    totalRow = { label: "Price", value: formatPrice(price) };
  }

  const statusColor = STATUS_COLORS[status];
  const hasRows = Boolean(model || inputTokens !== null || totalRow || statusColor);

  return (
    <div className="w-64 py-1 text-left">
      <div className="flex items-center gap-2">
        <SpanIcon spanNode={spanNode} isPromptLayerTool={isPromptLayerTool} className="ml-0" />
        <div className="min-w-0">
          <p className="truncate text-[13px] font-semibold text-gray-900">
            {getSpanDisplayName(spanNode)}
          </p>
          <p className="text-[11px] text-gray-400">{subtitle}</p>
        </div>
      </div>
      {hasRows ? (
        <dl className="mt-2 flex flex-col gap-1.5 border-t border-gray-100 pt-2">
          {model ? (
            <InfoRow
              label="Model"
              value={
                <span className="flex items-center justify-end gap-1.5">
                  {provider ? <ProviderIcon provider={provider} /> : null}
                  <span className="truncate">{String(model)}</span>
                </span>
              }
            />
          ) : null}
          {inputTokens !== null ? (
            <InfoRow label="Input tokens" value={String(inputTokens)} />
          ) : null}
          {outputTokens !== null ? (
            <InfoRow label="Output tokens" value={String(outputTokens)} />
          ) : null}
          {cachedTokens !== null ? (
            <InfoRow label="Cached tokens" value={String(cachedTokens)} />
          ) : null}
          {statusColor ? (
            <InfoRow label="Status" value={<span className={statusColor}>{status}</span>} />
          ) : null}
          {totalRow ? <InfoRow label={totalRow.label} value={totalRow.value} emphasized /> : null}
        </dl>
      ) : null}
      {meta?.error_message ? (
        <p className="mt-2 break-words font-mono text-[11px] leading-4 text-red-600">
          {meta.error_message}
        </p>
      ) : null}
    </div>
  );
};
